/**
 * Portfolio project source of truth.
 * Used by the /portfolio index and the /portfolio/:slug detail pages.
 * Photography is placeholder until proprietary shoots arrive.
 */

const PHOTO = {
  // MICE
  conference: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1800&q=80',
  conferenceAlt: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1800&q=80',
  workshop: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1800&q=80',
  istanbulSkyline:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/c/cb/Historical_peninsula_and_modern_skyline_of_Istanbul.jpg/1920px-Historical_peninsula_and_modern_skyline_of_Istanbul.jpg',
  meetingTable:
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1800&q=80',

  // EVENTS
  galaCandlelit:
    'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1800&q=80',
  galaTablescape:
    'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1800&q=80',
  banquet:
    'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1800&q=80',
  bosphorusDusk:
    'https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1800&q=80',

  // WEDDINGS
  weddingCouple:
    'https://images.unsplash.com/photo-1519741497674-611481863552?w=1800&q=80',
  weddingCeremony:
    'https://images.unsplash.com/photo-1465495976277-4387d4b0e4a6?w=1800&q=80',
  weddingOutdoor:
    'https://images.unsplash.com/photo-1525772764200-be829a350797?w=1800&q=80',
  weddingTable:
    'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1800&q=80',
  capCappadocia:
    'https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?w=1800&q=80',
  capValley:
    'https://images.unsplash.com/photo-1495344517868-8ebaf0a2044a?w=1800&q=80',

  // GENERIC
  candleClose:
    'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1800&q=80',
  drone:
    'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1800&q=80',
  detail:
    'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1800&q=80',
};

const projects = [
  {
    slug: 'octapharma-eu-congress',
    category: 'MICE',
    chapterRoute: '/mice',
    size: 'large',
    cover: PHOTO.conference,
    venue: 'Mandarin Oriental',
    when: 'Spring 2024',
    title: 'Octapharma EU Congress.',
    titleAccent: 'Congress.',
    eyebrowDate: 'MANDARIN ORIENTAL · SPRING 2024',
    brief:
      'Three-day plenary plus eighteen breakouts and a closing gala for European haematology leaders, with multi-language live interpretation.',
    numbers: [
      { value: '220', label: 'Delegates' },
      { value: '18', label: 'Breakout Rooms' },
      { value: '96%', label: 'NPS Score' },
    ],
    sub:
      'A three-day European haematology gathering rebuilt around clarity, language access, and a closing gala that ended on speeches, not service.',
    briefBody:
      'Octapharma asked us to host their European haematology leadership for three days in Istanbul without the agency haze that usually surrounds congresses of this scale. They wanted a single point of contact, a stage that worked across four languages, and an arc that ended with a gala their senior board would actually want to stay for.',
    challenge:
      'The challenge was choreography. Eighteen breakouts inside one venue means eighteen room turnovers, three lunch services on parallel tracks, and a plenary that can never run more than four minutes late, or every translator booth, screen and side-door queue fractures at once. We had to design the day, not just book it.',
    intent:
      'We treated the congress like a single piece of theatre. The plenary stage was sized for clarity, not spectacle, flat lighting on the speakers, line-array sound calibrated for the back wall, and a translator booth grid placed where it could see every face in the room. The gala was set in a separate hall to mark the change of register: candlelit, lower ceiling, slower music, fewer logos. The same captain ran both rooms so nothing slipped between teams.',
    essay: [
      { src: PHOTO.conference, caption: 'Plenary, twelve minutes before doors.' },
      { src: PHOTO.conferenceAlt, caption: 'A speaker reading the back wall, not the screen.' },
      { src: PHOTO.workshop, caption: 'Breakout four, hours before lunch arrived.' },
      { src: PHOTO.meetingTable, caption: 'Run-of-show on the captain\'s desk.' },
      { src: PHOTO.istanbulSkyline, caption: 'Istanbul, the long view delegates flew in for.' },
      { src: PHOTO.galaCandlelit, caption: 'Gala held the room without raising its voice.' },
      { src: PHOTO.galaTablescape, caption: 'A table set for the people speaking last.' },
      { src: PHOTO.detail, caption: 'A glass that travelled from the brief into the night.' },
    ],
    credits: {
      Venue: 'Mandarin Oriental Bosphorus',
      Catering: 'Mandarin Oriental in-house',
      AV: 'Audeon Pro Istanbul',
      Florals: 'Aksel Atelier',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
  {
    slug: 'muslim-hands-annual',
    category: 'MICE',
    chapterRoute: '/mice',
    size: 'small',
    cover: PHOTO.conferenceAlt,
    venue: 'Hilton Bosphorus',
    when: 'Autumn 2024',
    title: 'Muslim Hands Annual.',
    titleAccent: 'Annual.',
    eyebrowDate: 'HILTON BOSPHORUS · AUTUMN 2024',
    brief:
      'Mixed plenary, workshop and cultural-evening programme across three venues, in two languages.',
    numbers: [
      { value: '300', label: 'Attendees' },
      { value: '3', label: 'Venues' },
      { value: '2026', label: 'Re-booked' },
    ],
    sub:
      'A British charity\'s annual strategy gathering, run across three Istanbul venues with two working languages and a single delivery captain.',
    briefBody:
      'Muslim Hands runs its annual leadership gathering once a year and uses it to set the next twelve months of programme decisions. They asked for three formats inside one trip, a working plenary, structured workshops, and a cultural evening, without any of the three feeling like a side activity to the others.',
    challenge:
      'The harder problem was not the agenda, it was the move between venues. Three hundred attendees across three Istanbul addresses means three transfer windows where punctuality decides whether the next session starts on time or starts late. We designed the timing first and the venues second.',
    intent:
      'Each venue was chosen for what its room could do best. The Hilton ballroom held the plenary because it stays acoustically calm with three hundred people in it. The Tophane workshop space gave the small-group sessions a different temperature on purpose, lower ceiling, daylight, no microphones. The cultural evening moved out to a Bosphorus terrace so the day finished on the water, not in a hotel corridor. The captain rode every transfer.',
    essay: [
      { src: PHOTO.conferenceAlt, caption: 'Plenary set to read clean from the back row.' },
      { src: PHOTO.workshop, caption: 'Workshops with no microphones on purpose.' },
      { src: PHOTO.meetingTable, caption: 'A captain\'s desk between two venues.' },
      { src: PHOTO.istanbulSkyline, caption: 'The route between three Istanbul addresses.' },
      { src: PHOTO.bosphorusDusk, caption: 'Cultural evening, the water doing the work.' },
      { src: PHOTO.galaTablescape, caption: 'A table that finished the day, didn\'t define it.' },
      { src: PHOTO.banquet, caption: 'Quiet hospitality, deliberately under-styled.' },
      { src: PHOTO.candleClose, caption: 'Light, kept warm enough to talk across.' },
    ],
    credits: {
      Venue: 'Hilton Istanbul Bosphorus + Tophane Cultural Centre',
      Catering: 'Hilton in-house + Asitane (cultural evening)',
      AV: 'Audeon Pro Istanbul',
      Florals: 'Forthcoming',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
  {
    slug: 'messe-stuttgart-inbound',
    category: 'MICE',
    chapterRoute: '/mice',
    size: 'small',
    cover: PHOTO.istanbulSkyline,
    venue: 'Istanbul & Bursa',
    when: 'Winter 2024',
    title: 'Messe Stuttgart Inbound.',
    titleAccent: 'Inbound.',
    eyebrowDate: 'ISTANBUL & BURSA · WINTER 2024',
    brief:
      'Eight curated technical site visits across two cities, designed around partnership conversations.',
    numbers: [
      { value: '180', label: 'Delegates' },
      { value: '8', label: 'Site Visits' },
      { value: '8', label: 'MoUs Signed' },
    ],
    sub:
      'A German industrial trade-show delegation led across Istanbul and Bursa with one purpose: partnership conversations that ended in signatures.',
    briefBody:
      'Messe Stuttgart wanted to bring 180 of their senior industrial delegates into Türkiye for a week, with the explicit objective of leaving with signed memoranda. They needed access to manufacturers, regulators and logistics partners, and a programme that put German pragmatism and Turkish hospitality in the same room without diluting either.',
    challenge:
      'The challenge was credibility on both sides. The German delegation needed factory-floor access on punctual schedules. The Turkish counterparts needed cultural and protocol coverage that respected their seniority. We had to design site visits that felt operational, not ceremonial, and a hospitality arc that closed each day on the right note.',
    intent:
      'We split each day into two halves. Mornings were technical: factory walks in Bursa, regulatory briefings in Istanbul, partner pitches in board rooms. Afternoons were relational: curated lunches, walking tours of the historical peninsula, an evening dinner at a partner-suggested restaurant rather than a hotel banquet hall. Translators worked all twelve hours. By Thursday the room was already drafting MoUs.',
    essay: [
      { src: PHOTO.istanbulSkyline, caption: 'The peninsula, between meetings.' },
      { src: PHOTO.workshop, caption: 'A factory floor briefing in Bursa.' },
      { src: PHOTO.meetingTable, caption: 'Boardroom one, mid-morning.' },
      { src: PHOTO.conferenceAlt, caption: 'Partner pitches with two languages live.' },
      { src: PHOTO.bosphorusDusk, caption: 'Dinner where the day actually ended.' },
      { src: PHOTO.banquet, caption: 'A table set to keep talking across.' },
      { src: PHOTO.galaTablescape, caption: 'Closing dinner of the inbound week.' },
      { src: PHOTO.detail, caption: 'A pen passed across a draft.' },
    ],
    credits: {
      Venue: 'Various, boardrooms, factories, partner restaurants',
      Catering: 'Curated per venue',
      AV: 'Audeon Pro Istanbul',
      Florals: 'n/a',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
  {
    slug: 'liya-island-anniversary',
    category: 'EVENTS',
    chapterRoute: '/events',
    size: 'large',
    cover: PHOTO.galaCandlelit,
    venue: 'Liya Island',
    when: 'June 2024',
    title: 'Liya Island Anniversary.',
    titleAccent: 'Anniversary.',
    eyebrowDate: 'LIYA ISLAND · JUNE 2024',
    brief:
      'Brand-anniversary evening with a multi-course dinner, candlelit scenography on the cove and a surprise drone show closing the night.',
    numbers: [
      { value: '180', label: 'Guests' },
      { value: '1', label: 'Cove' },
      { value: '100%', label: 'Stayed for After-Party' },
    ],
    sub:
      'A brand anniversary built as a single cinematic evening on a private cove, where the lighting plot was the run-of-show.',
    briefBody:
      'The client wanted to mark twenty years on the same day they opened, twenty years earlier, and they wanted the evening to feel like the inside of a memory rather than a corporate function. They asked for one cove, one long table, no stage, no podium speeches. A photograph at the end of the night that would still hold up in five years.',
    challenge:
      'The challenge was the weather window and the silence. Liya Island has a single approach jetty, which meant guests, vendors, AV and catering all moved through the same gate within a four-hour build. And the brief asked for ambient music only, no MC, no microphones, no announcements, so every cue had to land through scenography, not voice.',
    intent:
      'We designed the night as three light states. Arrival was warm and low, lanterns lining the path from the jetty, candlelight on the long table, the cove kept dark intentionally. Dinner held that key for two hours. Then, at exactly 22:48, the cove lit up: 180 drones flew the brand mark over the water for ninety seconds, then disappeared. The after-party started in their wake, on the same cove, with no transition speech. The room kept its own rhythm.',
    essay: [
      { src: PHOTO.galaCandlelit, caption: 'Long table set, hours before guests stepped onto the jetty.' },
      { src: PHOTO.galaTablescape, caption: 'A place setting that was meant to be photographed close.' },
      { src: PHOTO.candleClose, caption: 'Candlelight tuned to one specific frame.' },
      { src: PHOTO.bosphorusDusk, caption: 'Cove kept dark on purpose, until 22:48.' },
      { src: PHOTO.drone, caption: 'Ninety seconds of the brand mark above the water.' },
      { src: PHOTO.banquet, caption: 'Dessert, eaten without anyone announcing it.' },
      { src: PHOTO.detail, caption: 'A glass raised to nothing in particular.' },
      { src: PHOTO.galaTablescape, caption: 'After-party, starting where dinner left off.' },
    ],
    credits: {
      Venue: 'Liya Island, private cove',
      Catering: 'GlowMeets-curated chef\'s team',
      AV: 'Audeon Pro Istanbul',
      Florals: 'Aksel Atelier',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
  {
    slug: 'ciragan-palace-ceremony',
    category: 'WEDDINGS',
    chapterRoute: '/weddings',
    size: 'large',
    cover: PHOTO.weddingCouple,
    venue: 'Çırağan Palace Kempinski',
    when: 'June 2024',
    title: 'Çırağan Palace Ceremony.',
    titleAccent: 'Ceremony.',
    eyebrowDate: 'ÇIRAĞAN PALACE KEMPINSKI · JUNE 2024',
    brief:
      'Imperial Bosphorus terrace ceremony and three-day arc with multilingual officiant and guests from eleven countries.',
    numbers: [
      { value: '220', label: 'Guests' },
      { value: '11', label: 'Countries' },
      { value: '3', label: 'Day Arc' },
    ],
    sub:
      'A three-day Bosphorus wedding for two families across eleven countries, anchored around a single terrace ceremony at golden hour.',
    briefBody:
      'The two families had been planning the wedding for two years. They wanted a ceremony on the imperial terrace at Çırağan Palace, a multilingual officiant who could carry English, Turkish and Arabic without breaking rhythm, and a three-day arc that gave guests from outside Türkiye time to actually see the city before the ceremony.',
    challenge:
      'The challenge was the terrace itself. It is sixteen metres deep with a hard wind line at the seventh row when the Bosphorus picks up after sunset. We had to design seating, sound and floral arrangement that read clean in still air at 18:30 and still held at 20:15 when the wind crossed. And every protocol detail had to clear two cultural reviews, not one.',
    intent:
      'We treated the ceremony as the centre of a longer composition. Day one was the welcome dinner indoors, relaxed, off-protocol, bilingual MC. Day two was the ceremony itself, set to begin precisely at 19:42 so the officiant\'s opening line landed under golden-hour light, with floral architecture sized to act as a windbreak rather than as decoration. Day three was a Bosphorus brunch and a city tour for the international guests. The same captain held all three days.',
    essay: [
      { src: PHOTO.weddingCouple, caption: 'A first photograph, kept private until the recessional.' },
      { src: PHOTO.weddingCeremony, caption: 'The terrace at 19:38, four minutes before vows.' },
      { src: PHOTO.weddingOutdoor, caption: 'Florals built to break the wind, not the photograph.' },
      { src: PHOTO.weddingTable, caption: 'Reception laid, indoors, one hour before guests sat.' },
      { src: PHOTO.bosphorusDusk, caption: 'The water, between ceremony and dinner.' },
      { src: PHOTO.galaCandlelit, caption: 'Speeches that were short on purpose.' },
      { src: PHOTO.galaTablescape, caption: 'A long table that ran past the bar, deliberately.' },
      { src: PHOTO.candleClose, caption: 'Last light of day three.' },
    ],
    credits: {
      Venue: 'Çırağan Palace Kempinski',
      Catering: 'Çırağan Palace in-house',
      AV: 'Audeon Pro Istanbul',
      Florals: 'Aksel Atelier',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
  {
    slug: 'argos-in-cappadocia',
    category: 'WEDDINGS',
    chapterRoute: '/weddings',
    size: 'small',
    cover: PHOTO.capCappadocia,
    venue: 'Argos in Cappadocia',
    when: 'October 2024',
    title: 'Argos in Cappadocia.',
    titleAccent: 'Cappadocia.',
    eyebrowDate: 'ARGOS IN CAPPADOCIA · OCTOBER 2024',
    brief:
      'Welcome dinner in caves, sunrise balloon photography, valley reception across three nights.',
    numbers: [
      { value: '95', label: 'Guests' },
      { value: '3', label: 'Nights' },
      { value: '14', label: 'Re-booked Türkiye' },
    ],
    sub:
      'A three-night wedding pinned to the rhythm of Cappadocia, caves at dusk, balloons at sunrise, the valley at twilight.',
    briefBody:
      'The couple wanted Cappadocia not as a backdrop but as the dramaturgy of the wedding itself. They asked us to programme the days around the landscape\'s natural light: the caves of Argos for the welcome dinner, the valley at sunrise for the photography, the open terrace for the reception when the rocks turned amber.',
    challenge:
      'The hard edge was logistics. Cappadocia means dispersed venues, narrow valley roads, and a sunrise photography window measured in twenty minutes, not flexible. We also had ninety-five guests arriving from four cities, with a hot-air-balloon flight that depends on a wind reading taken three hours before lift-off. None of it tolerates improvisation.',
    intent:
      'We built the wedding around the light, not the floor plan. The welcome dinner was set deep inside the cave restaurant at Argos so the ceiling carried the candlelight, not floral arches. The ceremony itself was a small, intentional moment on the terrace at golden hour, kept under thirty minutes. The reception spread across the valley as the rocks softened to amber. Day three opened with a 05:14 lift-off, guests in the basket, the couple in a second balloon, photography taken from a third. The whole arc fits in three nights, exactly.',
    essay: [
      { src: PHOTO.capCappadocia, caption: 'Sunrise, the moment the wind reading came back.' },
      { src: PHOTO.capValley, caption: 'The valley between ceremony and dinner.' },
      { src: PHOTO.weddingCeremony, caption: 'Vows on a terrace that didn\'t need florals.' },
      { src: PHOTO.weddingOutdoor, caption: 'Welcome dinner inside the cave restaurant.' },
      { src: PHOTO.weddingTable, caption: 'A table laid where the candlelight bounced once.' },
      { src: PHOTO.weddingCouple, caption: 'Couple, before the second balloon lifted.' },
      { src: PHOTO.banquet, caption: 'Reception, as the rocks turned amber.' },
      { src: PHOTO.candleClose, caption: 'Last night of the three.' },
    ],
    credits: {
      Venue: 'Argos in Cappadocia',
      Catering: 'Seki Restaurant, Argos in-house',
      AV: 'Audeon Pro Cappadocia partner',
      Florals: 'Aksel Atelier travel team',
      Photography: 'Forthcoming',
      Scenography: 'GlowMeets in-house',
    },
  },
];

export default projects;
